import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  ApiObject,
  DocumentRequirement,
  Program,
  ProgramExam,
  School,
  StepInstance,
} from 'app/types';
import Text from 'common/components/Text/Text';

import styles from 'features/explore/school/SchoolDetail.module.scss';
import {
  MagooshBrand,
  MagooshPercentage,
  MagooshExams,
  MagooshBannerDesktop,
  ExpertBannerStar,
  FirstExpertBannerIcon,
  SecondExpertBannerIcon,
  StepIcon,
  BenefitsIcon,
} from 'assets/explore';
import { useMediaQuery } from 'react-responsive';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from 'app/hooks';
import { SessionState } from 'features/session/sessionSlice';
import { checkOverflow, entityIsProgram, entityIsSchool, navigateTop } from 'common/utils';

interface CompProps {
  entity?: ApiObject<School> | ApiObject<Program>;
  applyRef: React.RefObject<HTMLDivElement>;
}

interface RequirementCardProps {
  title?: string;
  description?: string;
  some_required?: boolean;
  bachelor_message?: string;
  tooltip_info?: string;
  amount?: number;
  optional?: boolean;
}

interface StepCardProps {
  title?: string;
  mandatory?: boolean;
  position?: number;
}

interface CheckProps {
  text: string;
}
interface ExpertBannerProps {
  title: string;
  description: string;
  image?: string;
  height: string;
}

interface JourneyBannerProps {
  navigateTo: (route: string) => void;
  logged: boolean;
  isMobile: boolean;
}

const testsInfo = {
  'GMAT/GRE':
    'GMAT (Graduate Management Admission Test) and GRE (Graduate Record Examination) are\
   standardized tests used as admissions requirements for graduate-level programs, with GMAT primarily \
   for business schools and GRE for a variety of graduate programs.',
  GPA: "GPA (Grade Point Average) is a numerical representation of a student's academic performance,\
    calculated by averaging the grades received in all classes taken. The common measure is the US \
    standard that goes from 1 to 4.",
  'IELTS/TOEFL':
    'IELTS (International English Language Testing System) and TOEFL (Test of English as a Foreign Language)\
    are standardized tests used to assess the English language proficiency of non-native speakers, \
    often required for admission to universities and other institutions where English is the medium \
    of instruction. Other similar tests are the PTE and some universities accept the Duolingo English Test.',
  WorkingExperience:
    "Working experience requirements will depend on each program, while some don't require any \
    experience, others can require a minimum amount of years working in a specific field.",
};

const RequirementCard = ({
  title,
  description,
  some_required,
  bachelor_message,
  tooltip_info,
  amount,
  optional,
}: RequirementCardProps): React.ReactElement => {
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const tooltip = <Tooltip id="tooltip">{tooltip_info}</Tooltip>;
  const transformDescription = () => {
    if (description === '0.0' || description === undefined) return '';
    return `${description} ${description && some_required ? '*' : ''}`;
  };
  return (
    <div
      className={`card h-100 ${styles.requirementContainer}`}
      style={{ zIndex: isMobile ? '4' : 'auto', position: 'relative' }}
    >
      <div className="d-flex flex-row justify-content-between">
        <Text
          size={isMobile ? 14 : 18}
          weight={600}
          inlineStyle={{
            marginBottom: '4px',
            color: '#19234F',
            display: 'flex',
            justifyContent: 'space-between',
            maxWidth: '60%',
          }}
        >
          {title}
          {amount != undefined && amount > 1 && ` X${amount}`}
        </Text>
        {tooltip_info && (
          <OverlayTrigger placement="bottom" overlay={tooltip}>
            <i
              className="bi bi-info-circle"
              style={{ color: '#727D9B', position: 'absolute', top: '10px', right: '10px' }}
            ></i>
          </OverlayTrigger>
        )}
      </div>
      <Text size={14} weight={400} inlineStyle={{ color: '#727D9B' }}>
        {transformDescription()}
      </Text>
      {some_required && bachelor_message === '' && (
        <Text size={12} weight={400} inlineStyle={{ color: '#727D9B', marginTop: '4px' }}>
          * Only some programs require.
        </Text>
      )}

      {optional && (
        <Text
          size={isMobile ? 12 : 14}
          weight={400}
          inlineStyle={{ color: '#727D9B', marginTop: '4px' }}
        >
          (Optional)
        </Text>
      )}
      {bachelor_message && (
        <Text size={12} weight={400} inlineStyle={{ color: '#727D9B', marginTop: '4px' }}>
          {bachelor_message}
        </Text>
      )}
    </div>
  );
};

const StepCard = ({ title, mandatory, position }: StepCardProps): React.ReactElement => {
  const isMobile = useMediaQuery({ maxWidth: 768 });
  return (
    <div
      className={`card h-100 ${styles.stepContainer}`}
      style={isMobile ? { zIndex: '4' } : { zIndex: 'auto' }}
    >
      <img src={StepIcon} className={styles.stepIcon}></img>
      <Text
        size={isMobile ? 14 : 18}
        weight={600}
        inlineStyle={{
          marginBottom: '4px',
          color: '#19234F',
        }}
      >
        {`Step ${position}`}
      </Text>
      {!mandatory && (
        <Text size={14} weight={400} inlineStyle={{ color: '#727D9B' }}>
          (Optional)
        </Text>
      )}
      <Text size={14} weight={400} inlineStyle={{ color: '#727D9B' }}>
        {title}
      </Text>
    </div>
  );
};

const ExpertBannerComponent = ({
  title,
  description,
  image,
  height,
}: ExpertBannerProps): React.ReactElement => {
  const isMobile = useMediaQuery({ maxWidth: 768 });
  return (
    <div className={styles.expertBannerContainer}>
      <Text
        size={isMobile ? 14 : 20}
        weight={600}
        inlineStyle={
          isMobile
            ? {
                color: '#727D9B',
                maxWidth: '60%',
                marginBottom: '4px',
              }
            : { color: '#727D9B', maxWidth: 'inherit', marginBottom: '2px' }
        }
      >
        {title}
      </Text>
      <Text
        size={isMobile ? 16 : 20}
        weight={600}
        inlineStyle={
          isMobile
            ? {
                color: '#19234F',
                maxWidth: '60%',
                marginBottom: '16px',
              }
            : { color: '#19234F', maxWidth: '60%', marginBottom: '33px' }
        }
      >
        {description}
      </Text>
      <button
        className={`btn btn-primary btn-lg ${styles.expertBannerButton}`}
        onClick={() => window.open('/experts/request-meeting')}
      >
        Contact
      </button>
      <img src={ExpertBannerStar} className={styles.expertBannerStar}></img>
      {isMobile ? (
        <img src={image} className={styles.expertBannerIcon}></img>
      ) : (
        <img src={image} className={styles.expertBannerIcon} style={{ height: height }}></img>
      )}
    </div>
  );
};

const CheckComponent = ({ text }: CheckProps): React.ReactElement => {
  return (
    <div className={styles.checkContainer}>
      <i className="bi bi-check-circle-fill"> </i>
      <p> {text} </p>
    </div>
  );
};

const JourneyBanner = ({ navigateTo, logged, isMobile }: JourneyBannerProps) => {
  return (
    <div className={styles.journeyBannerContainer}>
      <Text
        size={isMobile ? 16 : 20}
        weight={600}
        inlineStyle={{ color: '#19234F', maxWidth: '60%' }}
      >
        Ready to start your application journey?
      </Text>
      <Text
        size={isMobile ? 16 : 20}
        weight={600}
        inlineStyle={{ color: '#19234F', marginBottom: '22px' }}
      >
        Unify support you all the way!
      </Text>
      <CheckComponent text={'Personalized matching to the best programs for you.'}></CheckComponent>
      <CheckComponent
        text={'Free guidance through the complete application process.'}
      ></CheckComponent>
      <CheckComponent text={"Discounts on many universities' application fees."}></CheckComponent>
      <CheckComponent
        text={'Fast track application in many of our partner universities.'}
      ></CheckComponent>
      <CheckComponent
        text={'Increased chances of getting accepted with our expert help.'}
      ></CheckComponent>
      <button
        className={`btn btn-primary btn-lg ${styles.buttonBanner}`}
        onClick={logged ? () => navigateTo('/home') : () => navigateTo('/signup')}
      >
        Join Unify!
      </button>
      <img src={BenefitsIcon} className={styles.benefitsIcon}></img>
    </div>
  );
};

const ApplySection = ({ entity, applyRef }: CompProps): React.ReactElement => {
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const [overflowStepsActive, setOverflowStepsActive] = useState<boolean>(false);
  const refScroll = useRef<any>();
  const navigate = useNavigate();

  const englishRequirementTitle = (entity: School | Program | undefined) => {
    const title = [];
    if (entityIsSchool(entity)) {
      if (entity.english_requirements?.toefl.required) title.push('TOEFL');
      if (entity.english_requirements?.ielts.required) title.push('IELTS');
      if (entity.english_requirements?.duolingo.required) title.push('DUOLINGO');
      if (entity.english_requirements?.pte_academic.required) title.push('PTE ACADEMIC');
    } else if (entityIsProgram(entity)) {
      if (programHasExam('TOEFL')) title.push('TOEFL');
      if (programHasExam('IELTS')) title.push('IELTS');
      if (programHasExam('DUOLINGO')) title.push('DUOLINGO');
      if (programHasExam('PTE ACADEMIC')) title.push('PTE ACADEMIC');
    }
    return title.join('/');
  };

  const englishRequirementDescription = (entity: School | Program | undefined) => {
    const description = [];
    if (entityIsSchool(entity)) {
      if (entity.english_requirements?.toefl.range)
        description.push(entity.english_requirements?.toefl.range);
      if (entity.english_requirements?.ielts.range)
        description.push(entity.english_requirements?.ielts.range);
      if (entity.english_requirements?.duolingo.range)
        description.push(entity.english_requirements?.duolingo.range);
      if (entity.english_requirements?.pte_academic.range)
        description.push(entity.english_requirements?.pte_academic.range);
    } else if (entityIsProgram(entity)) {
      const toefl = programExam('TOEFL');
      const ielts = programExam('IELTS');
      const duolingo = programExam('DUOLINGO');
      const pte_academic = programExam('PTE ACADEMIC');
      if (toefl) description.push(toefl.min_score);
      if (ielts) description.push(ielts.min_score);
      if (duolingo) description.push(duolingo.min_score);
      if (pte_academic) description.push(pte_academic.min_score);
    }
    return description.join('/');
  };
  const scroll = (scrollOffset: number) => {
    refScroll.current ? (refScroll.current.scrollLeft += scrollOffset) : '';
  };
  const { logged } = useAppSelector(({ session }: { session: SessionState }) => session);
  const navigateTo = (route: string) => {
    navigate(route);
    navigateTop();
  };

  const programHasExam = (examName: string) => {
    return (
      entityIsProgram(entity?.data) &&
      entity?.data.program_exams.some(
        (programExam: ProgramExam) => programExam.exam.name === examName
      )
    );
  };

  const programExam = (examName: string) => {
    return entityIsProgram(entity?.data)
      ? entity?.data.program_exams.find((programExam) => programExam.exam.name === examName)
      : undefined;
  };

  const gpaMessage = (entity: School) => {
    const gotBachelors = entity.programs?.some((p: Program) => p.degree_type === 'bachelors');
    const gotMasters = entity.programs?.some((p: Program) => p.degree_type === 'masters');
    if (gotBachelors && gotMasters) return 'May require Bachelor degree.';
    if (gotMasters) return '* Bachelor degree required.';
  };
  useEffect(() => {
    if (checkOverflow(refScroll.current)) {
      setOverflowStepsActive(true);
      return;
    }

    setOverflowStepsActive(false);
  }, [overflowStepsActive, entity]);

  return (
    <div className={styles.studySectionContainer} id="apply" ref={applyRef}>
      <div className={styles.sectionTitle}>
        <div className={styles.blueBlock} />
        <h2 className={styles.titleText}>Apply</h2>
      </div>
      <h3 className={styles.subtitle}>Application requirements</h3>
      <div
        className={`${styles.requirementsContainer} ${
          isMobile
            ? 'row flex-row flex-nowrap mt-3 mb-3 pt-1'
            : 'row row-cols-1 row-cols-md-2 row-cols-lg-4 mt-4'
        }`}
      >
        {entityIsSchool(entity?.data) &&
          (entity?.data.technical_requirements?.gmat.required ||
            entity?.data.technical_requirements?.gre.required) && (
            <div className={styles.requirement}>
              <RequirementCard
                title={
                  entity?.data.technical_requirements?.gmat.required &&
                  entity?.data.technical_requirements?.gre.required
                    ? 'GMAT/GRE'
                    : entity?.data.technical_requirements?.gmat.required
                    ? 'GMAT'
                    : 'GRE'
                }
                description={
                  entity?.data.technical_requirements?.gmat.range &&
                  entity?.data.technical_requirements?.gre.range
                    ? `${entity.data.technical_requirements?.gmat.range}/${entity.data.technical_requirements?.gre.range}`
                    : entity?.data.technical_requirements?.gmat.range
                    ? entity.data.technical_requirements?.gmat.range
                    : entity.data.technical_requirements?.gre.range
                }
                some_required={
                  entity.data.technical_requirements?.gmat.some_required ||
                  entity.data.technical_requirements?.gre.some_required
                }
                tooltip_info={testsInfo['GMAT/GRE']}
              />
            </div>
          )}
        {(programHasExam('GMAT') || programHasExam('GRE')) && (
          <div className={styles.requirement}>
            <RequirementCard
              title={
                programHasExam('GMAT') && programHasExam('GRE')
                  ? 'GMAT/GRE'
                  : programHasExam('GMAT')
                  ? 'GMAT'
                  : 'GRE'
              }
              description={
                programHasExam('GMAT') && programHasExam('GRE')
                  ? `${programExam('GMAT')?.min_score}/${programExam('GRE')?.min_score}`
                  : programHasExam('GMAT')
                  ? programExam('GMAT')?.min_score.toString()
                  : programExam('GRE')?.min_score.toString()
              }
              some_required={
                programHasExam('GMAT') && programHasExam('GRE')
                  ? !(
                      programExam('GMAT')?.kind === 'required' &&
                      programExam('GRE')?.kind === 'required'
                    )
                  : programHasExam('GMAT')
                  ? !(programExam('GMAT')?.kind === 'required')
                  : !(programExam('GRE')?.kind === 'required')
              }
              tooltip_info={testsInfo['GMAT/GRE']}
            />
          </div>
        )}
        {entityIsSchool(entity?.data) && entity?.data.gpa_requirement?.required && (
          <div className={styles.requirement}>
            <RequirementCard
              title={'GPA'}
              description={entity.data.gpa_range}
              bachelor_message={gpaMessage(entity.data)}
              some_required={entity?.data.gpa_requirement.required}
              tooltip_info={testsInfo['GPA']}
            />
          </div>
        )}
        {programHasExam('GPA') && (
          <div className={styles.requirement}>
            <RequirementCard
              title={'GPA'}
              description={programExam('GPA')?.min_score.toString()}
              bachelor_message={'* Bachelor degree required.'}
              some_required={!(programExam('GPA')?.kind === 'required')}
              tooltip_info={testsInfo['GPA']}
            />
          </div>
        )}
        {entityIsSchool(entity?.data) &&
          (entity?.data.english_requirements?.toefl.required ||
            entity?.data.english_requirements?.ielts.required ||
            entity?.data.english_requirements?.duolingo.required ||
            entity?.data.english_requirements?.pte_academic.required) && (
            <div className={styles.requirement}>
              <RequirementCard
                title={englishRequirementTitle(entity?.data)}
                description={englishRequirementDescription(entity?.data)}
                tooltip_info={testsInfo['IELTS/TOEFL']}
                bachelor_message={
                  'Minimum scores and required tests can vary depending on the program'
                }
              />
            </div>
          )}
        {(programHasExam('TOEFL') ||
          programHasExam('IELTS') ||
          programHasExam('DUOLINGO') ||
          programHasExam('PTE ACADEMIC')) && (
          <div className={styles.requirement}>
            <RequirementCard
              title={englishRequirementTitle(entity?.data)}
              description={englishRequirementDescription(entity?.data)}
              some_required={
                programHasExam('TOEFL') && programHasExam('IELTS')
                  ? !(
                      programExam('TOEFL')?.kind === 'required' &&
                      programExam('IELTS')?.kind === 'required'
                    )
                  : programHasExam('TOEFL')
                  ? !(programExam('TOEFL')?.kind === 'required')
                  : !(programExam('IELTS')?.kind === 'required')
              }
              tooltip_info={testsInfo['IELTS/TOEFL']}
              bachelor_message={
                'Minimum scores and required tests can vary depending on the program'
              }
            />
          </div>
        )}
        {entityIsSchool(entity?.data) &&
          entity?.data.min_working_experience_required != undefined && (
            <div className={styles.requirement}>
              <RequirementCard
                title={'Working experience'}
                description={
                  entity.data.min_working_experience_required === 0
                    ? 'No experience required'
                    : `${entity.data.min_working_experience_required} ${
                        entity.data.min_working_experience_required === 1 ? 'year' : 'years'
                      }`
                }
                tooltip_info={testsInfo['WorkingExperience']}
              />
            </div>
          )}
        {entityIsProgram(entity?.data) && entity?.data.work_experience_min != undefined && (
          <div className={styles.requirement}>
            <RequirementCard
              title={'Working experience'}
              description={
                entity.data.work_experience_min === 0
                  ? 'No experience required'
                  : `${entity.data.work_experience_min} ${
                      entity.data.work_experience_min === 1 ? 'year' : 'years'
                    }`
              }
              tooltip_info={testsInfo['WorkingExperience']}
            />
          </div>
        )}
      </div>
      {entityIsSchool(entity?.data) && entity?.data.application_description && (
        <Text
          size={isMobile ? 14 : 16}
          weight={400}
          inlineStyle={{ color: '#19234F', marginBottom: '32px' }}
        >
          {entity?.data.application_description}
        </Text>
      )}
      {entityIsProgram(entity?.data) && entity?.data.school.application_description && (
        <Text
          size={isMobile ? 14 : 16}
          weight={400}
          inlineStyle={{ color: '#19234F', marginBottom: '32px' }}
        >
          {entity?.data.school.application_description}
        </Text>
      )}
      <div className={styles.magooshContainer}>
        {isMobile ? (
          <>
            <div className={styles.headerBanner}>
              <img src={MagooshBrand}></img>
              <img src={MagooshPercentage}></img>
            </div>
            <Text
              size={isMobile ? 16 : 20}
              weight={600}
              inlineStyle={
                isMobile
                  ? {
                      color: '#19234F',
                    }
                  : {
                      display: 'flex',
                      flexDirection: 'column',
                      color: '#19234F',
                      maxWidth: 'inherit',
                    }
              }
            >
              {`helps you to crack all exams!`}
            </Text>
            <img src={MagooshExams}></img>
            <a
              className={`btn btn-primary btn-lg ${styles.magooshButton}`}
              href="http://imp.i154272.net/6bYLeN"
              target="_blank"
              rel="noopener noreferrer"
            >
              Explore now
            </a>
          </>
        ) : (
          <div className={styles.magooshBannerDesktop}>
            <div className={styles.magooshBannerSection}>
              <div className={styles.headerBanner}>
                <img src={MagooshBrand}></img>
              </div>
              <Text
                size={isMobile ? 16 : 20}
                weight={600}
                inlineStyle={
                  isMobile
                    ? {
                        color: '#19234F',
                      }
                    : {
                        display: 'flex',
                        flexDirection: 'column',
                        color: '#19234F',
                        maxWidth: 'inherit',
                        marginBottom: '28px',
                      }
                }
              >
                {`helps you to crack all exams!`}
              </Text>
              <a
                className={`btn btn-primary btn-lg ${styles.magooshButton}`}
                href="http://imp.i154272.net/6bYLeN"
                target="_blank"
                rel="noopener noreferrer"
              >
                Explore now
              </a>
            </div>
            <div className={styles.magooshBannerSection}>
              <img src={MagooshBannerDesktop}></img>
            </div>
          </div>
        )}
      </div>
      {!!(
        (entity?.data.document_requirements && entity?.data.document_requirements.length > 0) ||
        (entityIsProgram(entity?.data) &&
          entity?.data.school.document_requirements &&
          entity?.data.school.document_requirements.length > 0)
      ) && (
        <>
          <h3 className={styles.subtitle}>Required documents</h3>
          {isMobile ? (
            <div
              className={`${styles.requirementsContainer} row flex-row flex-nowrap mt-3 mb-3 pt-1`}
            >
              {entity?.data.document_requirements.length > 0
                ? entity.data.document_requirements.map(
                    (document_requirement: DocumentRequirement) => (
                      <div className={styles.requirement} key={document_requirement.id}>
                        <RequirementCard
                          title={document_requirement.document.name}
                          tooltip_info={
                            document_requirement.description
                              ? document_requirement.description
                              : document_requirement.document.description
                          }
                          amount={document_requirement.amount}
                          optional={document_requirement.any_optional}
                        />
                      </div>
                    )
                  )
                : entityIsProgram(entity?.data) &&
                  entity.data.school.document_requirements.map(
                    (document_requirement: DocumentRequirement) => (
                      <div className={styles.requirement} key={document_requirement.id}>
                        <RequirementCard
                          title={document_requirement.document.name}
                          tooltip_info={
                            document_requirement.description
                              ? document_requirement.description
                              : document_requirement.document.description
                          }
                          amount={document_requirement.amount}
                          optional={document_requirement.any_optional}
                        />
                      </div>
                    )
                  )}
            </div>
          ) : (
            <div
              className={`${styles.requirementsContainer} row row-cols-1 row-cols-md-2 row-cols-lg-4 mt-4`}
            >
              {entity?.data.document_requirements.length > 0
                ? entity.data.document_requirements.map(
                    (document_requirement: DocumentRequirement) => (
                      <div className={`col ${styles.requirement}`} key={document_requirement.id}>
                        <RequirementCard
                          title={document_requirement.document.name}
                          tooltip_info={
                            document_requirement.description
                              ? document_requirement.description
                              : document_requirement.document.description
                          }
                          amount={document_requirement.amount}
                          optional={document_requirement.any_optional}
                        />
                      </div>
                    )
                  )
                : entityIsProgram(entity?.data) &&
                  entity.data.school.document_requirements.map(
                    (document_requirement: DocumentRequirement) => (
                      <div className={styles.requirement} key={document_requirement.id}>
                        <RequirementCard
                          title={document_requirement.document.name}
                          tooltip_info={
                            document_requirement.description
                              ? document_requirement.description
                              : document_requirement.document.description
                          }
                          amount={document_requirement.amount}
                          optional={document_requirement.any_optional}
                        />
                      </div>
                    )
                  )}
            </div>
          )}
        </>
      )}
      {isMobile ? (
        <>
          <ExpertBannerComponent
            title="Our partner"
            description="Contact the admission team of the university if you have any doubts"
            image={FirstExpertBannerIcon}
            height="80px"
          />
          <ExpertBannerComponent
            title="Our Service"
            description="Our expert can review all your documents!"
            image={SecondExpertBannerIcon}
            height="100px"
          />
        </>
      ) : (
        <div className="d-flex flex-row justify-content-between gap-3">
          <ExpertBannerComponent
            title="Our partner"
            description="Contact the admission team of the university if you have any doubts"
            image={FirstExpertBannerIcon}
            height="80px"
          />
          <ExpertBannerComponent
            title="Our Service"
            description="Our expert can review all your documents!"
            image={SecondExpertBannerIcon}
            height="100px"
          />
        </div>
      )}
      {!!(
        (entity?.data.step_instances && entity?.data.step_instances.length > 0) ||
        (entityIsProgram(entity?.data) &&
          entity?.data.school.step_instances &&
          entity?.data.school.step_instances.length > 0)
      ) && (
        <>
          <h3
            className={styles.subtitle}
            style={{ display: 'flex', justifyContent: 'space-between' }}
          >
            Apply step by step
            {overflowStepsActive && (
              <div className={styles.arrowsContainer}>
                <div className={styles.arrowContainer} onClick={() => scroll(-150)}>
                  <i className="bi bi-chevron-compact-left"></i>
                </div>
                <div className={styles.arrowContainer} onClick={() => scroll(150)}>
                  <i className="bi bi-chevron-compact-right"></i>
                </div>
              </div>
            )}
          </h3>
          <div
            className={`${styles.requirementsContainer} row flex-row flex-nowrap mt-3 mb-3 pt-1`}
            ref={refScroll}
          >
            {entity?.data.step_instances.length > 0
              ? entity?.data.step_instances.map((step_instance: StepInstance) => (
                  <div className={styles.step} key={step_instance.id}>
                    <StepCard
                      title={step_instance.step.title}
                      mandatory={step_instance.mandatory}
                      position={step_instance.position}
                    />
                  </div>
                ))
              : entityIsProgram(entity?.data) &&
                entity?.data.school.step_instances.map((step_instance: StepInstance) => (
                  <div className={styles.step} key={step_instance.id}>
                    <StepCard
                      title={step_instance.step.title}
                      mandatory={step_instance.mandatory}
                      position={step_instance.position}
                    />
                  </div>
                ))}
          </div>
        </>
      )}
      <div className={styles.journeyBannerContainer}>
        <Text
          size={isMobile ? 16 : 20}
          weight={600}
          inlineStyle={{ color: '#19234F', maxWidth: '60%' }}
        >
          Ready to start your application journey?
        </Text>
        <Text
          size={isMobile ? 16 : 20}
          weight={600}
          inlineStyle={{ color: '#19234F', marginBottom: '22px' }}
        >
          Unify support you all the way!
        </Text>
        <CheckComponent
          text={'Personalized matching to the best programs for you.'}
        ></CheckComponent>
        <CheckComponent
          text={'Free guidance through the complete application process.'}
        ></CheckComponent>
        <CheckComponent text={"Discounts on many universities' application fees."}></CheckComponent>
        <CheckComponent
          text={'Fast track application in many of our partner universities.'}
        ></CheckComponent>
        <CheckComponent
          text={'Increased chances of getting accepted with our expert help.'}
        ></CheckComponent>
        <button
          className={`btn btn-primary btn-lg ${styles.buttonBanner}`}
          onClick={logged ? () => navigateTo('/home') : () => navigateTo('/signup')}
        >
          Join Unify!
        </button>
        <img src={BenefitsIcon} className={styles.benefitsIcon}></img>
      </div>
    </div>
  );
};

export { ApplySection, JourneyBanner };
